import fileBackendService from '@/services/formBuilder/fileBackendService.js';

//GET apis

//Get existing forms list with search keyword, filtered by category and time range, and paginated.
export async function listFormsApi({status='ACTIVE', category, search, start_date, end_date, limit=20, offset=0}) {
    try{
        const params = {
            status,
            ...category && { category },
            ...search && { search },
            ...start_date && { start_date },
            ...end_date && { end_date },
            limit,
            offset
        }
        const response = await fileBackendService.get('/forms', {params});
        return response.data;
    } catch (error) {
        console.error('Error listing forms:', error);
        throw new Error(error?.message ?? 'Failed to get forms list');
    }
}

// Get active form categories for dropdown display
export async function getCategoriesApi() {
    try{
        const response = await fileBackendService.get('/forms/categories');
        return response.data;
    } catch (error) {
        console.error('Error getting categories:', error);
        throw new Error(error?.message ?? 'Failed to get categories');
    }
}

// Get mappable form elements
export async function getMappableFormElementsApi() {
    try{
        const response = await fileBackendService.get(`/forms/mappable-variables`);
        return response.data;
    } catch (error) {
        console.error('Error getting mappable form elements:', error);
        throw new Error(error?.message ?? 'Failed to get mappable form elements');
    }
}

// Get form detail (latest version) including full definition and mapping status
export async function getFormByIdApi(formId) {
    try{
        const response = await fileBackendService.get(`/forms/${formId}`);
        return response.data;
    } catch (error) {
        console.error('Error getting form detail:', error);
        throw new Error(error?.message ?? 'Failed to get form detail');
    }
}

// List all version snapshots for a form (newest first)
export async function getFormVersionsApi(formId) {
    try{
        const response = await fileBackendService.get(`/forms/${formId}/versions`);
        return response.data;
    } catch (error) {
        console.error('Error getting form versions:', error);
        throw new Error(error?.message ?? 'Failed to get form versions');
    }
}

// Get a specific version snapshot with its field mappings
export async function getFormVersionByNumberApi(formId, version) {
    try{
        const response = await fileBackendService.get(`/forms/${formId}/versions/${version}`);
        return response.data;
    } catch (error) {
        console.error('Error getting form version:', error);
        throw new Error(error?.message ?? 'Failed to get form version');
    }
}


//POST apis

// Create a new form. Optionally clone from a template
export async function createFormApi({name, description, category, emoji_tag, template_id, definition}) {
    try{
        const body = {
            name,
            ...description && { description },
            ...category && { category },
            ...emoji_tag && { emoji_tag },
            ...template_id && { template_id },
            ...definition && { definition }
        };
        const response = await fileBackendService.post('/forms', body);
        return response.data;
    } catch (error) {
        console.error('Error creating form:', error);
        throw new Error(error?.message ?? 'Failed to create form');
    }
}

// Clone an existing form into a new form with "(Copy)" appended to the name
export async function duplicateFormApi(formId) {
    try{
        const response = await fileBackendService.post(`/forms/${formId}/duplicate`);
        return response.data;
    } catch (error) {
        console.error('Error duplicating form:', error);
        throw new Error(error?.message ?? 'Failed to duplicate form');
    }
}


//PUT apis

// Update a form. If definition changes, automatically increments version
export async function updateFormApi(formId, {name, description, category, emoji_tag, definition, change_summary}) {
    try{
        const body = {
            ...name && { name },
            ...description && { description },
            ...category && { category },
            ...emoji_tag && { emoji_tag },
            ...definition && { definition },
            ...change_summary && { change_summary }
        };
        const response = await fileBackendService.put(`/forms/${formId}`, body);
        return response.data;
    } catch (error) {
        console.error('Error updating form:', error);
        throw new Error(error?.message ?? 'Failed to update form');
    }
}

// Archive an active form (status: ACTIVE → ARCHIVED)
export async function archiveFormApi(formId) {
    try{
        const response = await fileBackendService.put(`/forms/${formId}/archive`);
        return response.data;
    } catch (error) {
        console.error('Error archiving form:', error);
        throw new Error(error?.message ?? 'Failed to archive form');
    }
}

// Restore an archived form (status: ARCHIVED → ACTIVE)
export async function restoreFormApi(formId) {
    try{
        const response = await fileBackendService.put(`/forms/${formId}/restore`);
        return response.data;
    } catch (error) {
        console.error('Error restoring form:', error);
        throw new Error(error?.message ?? 'Failed to restore form');
    }
}


// AI-powered mapping suggestion
// Accepts full form context and returns suggested mapping for a single field
export async function suggestMappingApi({ name, definition, field_id }) {
    try{
        const response = await fileBackendService.post('/ai/suggest-mappings', {
            name,
            definition,
            field_id
        });
        return response.data;
    } catch (error) {
        console.error('Error getting AI mapping suggestion:', error);
        throw new Error(error?.message ?? 'Failed to get AI mapping suggestion');
    }
}

// AI-powered form generation
// Generate a complete form definition from a natural language description
// Optionally accepts an existing definition to modify
export async function generateFormWithAIApi({ description, definition }) {
    try{
        const body = {
            description,
            ...definition && { definition }
        };
        const response = await fileBackendService.post('/ai/generate-form', body);
        return response.data;
    } catch (error) {
        console.error('Error generating form with AI:', error);
        throw new Error(error?.message ?? 'Failed to generate form with AI');
    }
}

//DELETE apis

// Soft-delete an archived form (status: ARCHIVED → DELETED)
export async function deleteFormApi(formId) {
    try{
        const response = await fileBackendService.delete(`/forms/${formId}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting form:', error);
        throw new Error(error?.message ?? 'Failed to delete form');
    }
}